import React from "react";
import styled from "@emotion/styled";
import PreviewSection from "../components/PreviewSection";
import { Button, CloseButton } from "@atlas-int/ui";
import type { ButtonVariant, ButtonSize } from "@atlas-int/ui";
import {
  spacing,
  fontSize,
  fontFamily,
  fontWeight,
  text,
} from "@atlas-int/tokens";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[4]};
  align-items: center;
  margin-bottom: ${spacing[6]};
`;

const GroupLabel = styled.h4`
  font-family: ${fontFamily.base};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${text.secondary};
  margin: ${spacing[6]} 0 ${spacing[2]} 0;
`;

const VariantGroup = styled.div`
  margin-bottom: ${spacing[8]};
`;

const VariantTitle = styled.h4`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.normal};
  color: ${text.muted};
  margin: 0 0 ${spacing[2]} 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// ─── Constants ───────────────────────────────────────────────────────────────

const variants: ButtonVariant[] = ["default", "primary", "ghost", "selector"];
const sizes: ButtonSize[] = ["sm", "md", "lg"];

// ─── Component ───────────────────────────────────────────────────────────────

const ButtonSection: React.FC = () => {
  return (
    <PreviewSection
      id="buttons"
      title="Button"
      description="All button variants, sizes, and states. The Button component supports four variants (default, primary, ghost, selector) and three sizes (sm, md, lg)."
    >
      {/* Variants x Sizes */}
      <GroupLabel>Variants &times; Sizes</GroupLabel>
      {variants.map((variant) => (
        <VariantGroup key={variant}>
          <VariantTitle>{variant}</VariantTitle>
          <ButtonRow>
            {sizes.map((size) => (
              <Button key={size} variant={variant} size={size}>
                {variant} {size}
              </Button>
            ))}
          </ButtonRow>
        </VariantGroup>
      ))}

      {/* Disabled states */}
      <GroupLabel>Disabled State</GroupLabel>
      <ButtonRow>
        {variants.map((variant) => (
          <Button key={variant} variant={variant} disabled>
            {variant}
          </Button>
        ))}
      </ButtonRow>

      {/* Selector: selected state */}
      <GroupLabel>Selector Variant &mdash; Selected vs Unselected</GroupLabel>
      <ButtonRow>
        <Button variant="selector" selected={false}>
          Unselected
        </Button>
        <Button variant="selector" selected={true}>
          Selected
        </Button>
      </ButtonRow>

      {/* CloseButton */}
      <GroupLabel>CloseButton</GroupLabel>
      <ButtonRow>
        <CloseButton size="sm" />
        <CloseButton size="md" />
        <CloseButton variant="overlay" size="sm" style={{ position: "relative" }} />
      </ButtonRow>
    </PreviewSection>
  );
};

export default ButtonSection;
